import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { memberController } from '../controllers/memberController';
import { hasLinkedMemberProfile } from '../utils/firstLoginFlow';
import { isMemberRequiredFieldsComplete } from '../utils/memberProfileCompletion';

const MemberProfileCompletionContext = createContext(null);

export function MemberProfileCompletionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const personId = useMemo(() => {
    if (!hasLinkedMemberProfile(user)) return '';
    return String(user?.member_id || user?.person_id || '').trim();
  }, [user]);

  const [complete, setComplete] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !personId) {
      setComplete(true);
      return true;
    }
    setLoading(true);
    try {
      const { member } = await memberController.fetchMemberForEdit(personId);
      const ok = isMemberRequiredFieldsComplete(member, { selfEdit: true });
      setComplete(ok);
      return ok;
    } catch (err) {
      console.error('Member profile completion check failed:', err);
      // Ne pas bloquer l'app si l'API People est indisponible.
      setComplete(true);
      return true;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, personId]);

  useEffect(() => {
    if (!isAuthenticated || !personId) {
      setComplete(true);
      return;
    }
    setComplete(null);
    refresh();
  }, [isAuthenticated, personId, refresh]);

  const value = useMemo(
    () => ({
      personId,
      complete: personId ? complete === true : true,
      loading: Boolean(personId && (loading || complete === null)),
      refresh,
    }),
    [personId, complete, loading, refresh]
  );

  return (
    <MemberProfileCompletionContext.Provider value={value}>
      {children}
    </MemberProfileCompletionContext.Provider>
  );
}

export function useMemberProfileCompletion() {
  const ctx = useContext(MemberProfileCompletionContext);
  if (!ctx) {
    throw new Error('useMemberProfileCompletion must be used within MemberProfileCompletionProvider');
  }
  return ctx;
}
